
import { throwError, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { DashboardModel, DashboardCustomerService, BillingProgressModel, ResidentProgressModel } from '../models/dashboard.model';
import { InvoiceModel } from '../models/invoices.model';
import { InternalPropertyTeamModel } from '../models/internal-property-team.model';
import { PreBillModel } from '../models/pre-bill.model';
import { MilestonesTreeViewModel } from '../models/milestone-tree-view.model';
import { CurrentDateModel } from 'app/models/current-date.model';
import { ConfigService } from './config.service';

// Model classes for individual API endpoints
export class BillingSummary {
  public billing_progress: BillingProgressModel;
}

export class ResidentSummary {
  public resident_progress: ResidentProgressModel;
}

export class CustomerServiceSummary {
  public customer_service: DashboardCustomerService;
}

export class InvoicesSummary {
  public current_invoice: InvoiceModel;
  public outstanding_invoices: Array<InvoiceModel>;
}

export class PreBillSummary {
  public current_pre_bill: PreBillModel;
  public pre_bill_milestone: MilestonesTreeViewModel;
}

export class PropertyTeamSummary {
  public internal_property_team: Array<InternalPropertyTeamModel>;
}

export class WorkflowsSummary {
  public workflows_count: number;
  public pending_on_demand_efb_count: number;
}

@Injectable()
export class DashboardService {

  constructor(private http: HttpClient, private config: ConfigService) { }

  private createCurrentDateModel(): CurrentDateModel {
    let model = new CurrentDateModel();
    model.current_date = this.config.getCurrentMoment().startOf('day').toDate();
    return model;
  }

  public GetDashboard(): Observable<DashboardModel> {
    let model = new CurrentDateModel();
    model.current_date = this.config.getCurrentMoment().startOf('day').toDate();

    return this.http.post<DashboardModel>(this.config.getApiRoute('api/v3/client-portal/dashboard'), model).pipe(
      map(data => {
        return data;
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(err.error.message);
      })
    );
  }

  public getBillingProgress(): Observable<BillingProgressModel> {
    let model = this.createCurrentDateModel();

    return this.http.post<BillingProgressModel>(
      this.config.getApiRoute('api/v3/client-portal/dashboard/billing-progress'), 
      model
    ).pipe(
      map(data => {
        return data;
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(err.error.message);
      })
    );
  }

  public getResidentProgress(): Observable<ResidentProgressModel> {
    let model = this.createCurrentDateModel();

    return this.http.post<ResidentProgressModel>(
      this.config.getApiRoute('api/v3/client-portal/dashboard/resident-progress'), 
      model
    ).pipe(
      map(data => {
        return data;
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(err.error.message);
      })
    );
  }

  public getCustomerService(): Observable<DashboardCustomerService> {
    let model = this.createCurrentDateModel();

    return this.http.post<DashboardCustomerService>(
      this.config.getApiRoute('api/v3/client-portal/dashboard/customer-service'), 
      model
    ).pipe(
      map(data => {
        return data;
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(err.error.message);
      })
    );
  }

  public getInvoicesSummary(): Observable<InvoicesSummary> {
    return this.http.get<InvoicesSummary>(
      this.config.getApiRoute('api/v3/client-portal/dashboard/invoices')
    ).pipe(
      map(data => {
        return data;
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(err.error.message);
      })
    );
  }

  public getPreBillSummary(): Observable<PreBillSummary> {
    return this.http.get<PreBillSummary>(
      this.config.getApiRoute('api/v3/client-portal/dashboard/pre-bill')
    ).pipe(
      map(data => {
        return data;
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(err.error.message);
      })
    );
  }

  public getPropertyTeam(): Observable<Array<InternalPropertyTeamModel>> {
    return this.http.get<Array<InternalPropertyTeamModel>>(
      this.config.getApiRoute('api/v3/client-portal/dashboard/property-team')
    ).pipe(
      map(data => {
        return data;
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(err.error.message);
      })
    );
  }

  public getWorkflowsSummary(): Observable<WorkflowsSummary> {
    return this.http.get<WorkflowsSummary>(
      this.config.getApiRoute('api/v3/client-portal/dashboard/workflows')
    ).pipe(
      map(data => {
        return data;
      }),
      catchError((err: HttpErrorResponse) => {
        return throwError(err.error.message);
      })
    );
  }
}